import { ComponentProps, forwardRef, useRef } from 'react'
import { useDrop } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'

import { pxToRem } from '@/shared/styles/themes'
import { faCloudArrowUp, faFile, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { CircularProgress, FormLabel, InputAdornment, Typography } from '@mui/material'

import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { TextField } from '../TextField/TextField'
import { formatFileSize } from './libs/formatFileSize'
import { StyledActions, StyledDropOverlay, StyledFileInputWrapper } from './StyledFileInputField'

export type FileInputFieldProps = Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'onError'> & {
  value?: File | null
  error?: string | null
  disabled?: boolean
  label?: string
  accept?: string
  maxSize?: number // in bytes
  loading?: boolean
  showFileSize?: boolean
  allowClear?: boolean
  onChange?: (file: File | null) => void
  onError?: (error: string) => void
}

export const FileInputField =  forwardRef<HTMLInputElement, FileInputFieldProps>(({
  id,
  value,
  error,
  disabled,
  label,
  accept = '*/*',
  maxSize,
  loading,
  showFileSize = true,
  allowClear = true,
  onChange,
  onError,
  ...props
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // Custom file drop handler with validation
  const handleFileDrop = (files: File[]) => {
    if (disabled || loading) return

    const file = files[0]

    if (!file) return

    // Validate file type
    if (accept && accept !== '*/*') {
      const acceptedTypes = accept.split(',').map(type => type.trim())
      const fileType = file.type
      const fileExtension = `.${file.name.split('.').pop()}`

      if (!acceptedTypes.some(type =>
        type.startsWith('.') ? fileExtension === type : fileType.startsWith(type.replace('*', '')),
      )) {
        onError?.('File type not accepted')

        return
      }
    }

    // Validate file size
    if (maxSize && file.size > maxSize) {
      onError?.(`File size must be less than ${formatFileSize(maxSize)}`)

      return
    }

    onChange?.(file)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.(null)

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleBrowse = () => {
    inputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])

    handleFileDrop(files)
  }


  // Dropzone for file drag and drop
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: [NativeTypes.FILE],
    drop(item: { files: File[] }) {
      handleFileDrop(item.files)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [])

  const isDragActive = isOver && canDrop

  return (
    <>
      <FormLabel>{label}</FormLabel>
      <StyledFileInputWrapper
        ref={drop}
        $isDragActive={isDragActive}
        $error={!!error}
        $disabled={disabled}
        id={id}
      >
        <input
          ref={inputRef}
          name="file"
          type="file"
          accept={accept}
          onChange={handleFileChange}
          style={{ visibility: 'hidden' }}
          {...props}
        />
        <TextField
          inputRef={ref}
          placeholder="No file chosen"
          value={value ? value.name : ''}
          error={!!error}
          disabled={disabled}
          helperText={(showFileSize && value) ? formatFileSize(value.size) : 'Select a file through the cloud button or drag and drop file here'}
          fullWidth
          inputProps={{
            readOnly: true,
            style: {
              cursor: 'default',
              userSelect: 'none',
            },
          }}
          InputProps={{
            startAdornment: value && (
              <InputAdornment position="start">
                <Icon sx={{ marginLeft: pxToRem(4), fontSize: pxToRem(20) }} icon={faFile} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <StyledActions>
                    {value && allowClear && (
                      <IconButton
                        size="small"
                        onClick={handleClear}
                        disabled={disabled}
                        tooltipTitle="Remove file"
                      >
                        <Icon sx={{ fontSize: pxToRem(14) }} icon={faXmark} />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={handleBrowse}
                      disabled={disabled || loading}
                      tooltipTitle="Upload file"
                    >
                      <Icon icon={faCloudArrowUp} />
                    </IconButton>
                  </StyledActions>
                )}
              </InputAdornment>
            ),
          }}
        />

        {isDragActive && (
          <StyledDropOverlay>
            <Typography variant="body2">Drop file here</Typography>
          </StyledDropOverlay>
        )}
      </StyledFileInputWrapper>
    </>
  )
})
